import { Button, Form, Layout, Steps } from 'antd';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { BOOKING_COM_URL_REGEXP } from 'app/components/commons/Hotel/MarketingBrief/HotelLinks/HotelLinks';
import { useSaveHotel } from 'app/hooks/data/hotels/useHotel';
import { ChannelManager } from 'app/typings/channelManagers';
import type { NewHotel as NewHotelType } from 'app/typings/hotels/NewHotel/NewHotel';
import { RateMode } from 'app/typings/rateModes';
import { isValidEmail } from 'app/utils/email';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import { FirstStep } from './FirstStep/FirstStep';
import { FourthStep } from './FourthStep/FourthStep';
import './NewHotel.scss';
import { SecondStep } from './SecondStep/SecondStep';
import { ThirdStep } from './ThirdStep/ThirdStep';
import { isSubCityRequired } from './utils';

type StepType = 0 | 1 | 2 | 3;

const { Content, Header } = Layout;
const { Step } = Steps;

const STEP_NUMBER = 4;
const BRIEF_PREFIX = 'brief';

const INITIAL_VALUE = {
  notifEmails: [{ email: '', role: undefined }],
};

const isStep1Completed = (hotel: Partial<NewHotelType> | null) => {
  if (!hotel) {
    return false;
  }

  const subCityRequired =
    hotel?.address?.city && isSubCityRequired(hotel.address?.city);

  return !!(
    hotel.name &&
    (hotel.stars || hotel.stars === 0) &&
    hotel.phone &&
    hotel.address &&
    hotel.address.street &&
    hotel.address.city &&
    hotel.address.department &&
    hotel.address.region &&
    hotel.address.zipCode &&
    hotel.address.countryId &&
    hotel.location?.coords?.lat &&
    hotel.location?.coords?.lng &&
    (!subCityRequired ||
      (hotel.address.subCity && hotel.address.subCity.trim()))
  );
};

const isStep2Completed = (hotel: Partial<NewHotelType> | null) => {
  if (!hotel || !hotel.notifEmails) {
    return false;
  }

  if (
    hotel.channelManager === ChannelManager.MEWS &&
    !hotel.channelManagerCredentials
  ) {
    return false;
  }

  const hasDuplicateEmails = (emails: string[]) => {
    const emailSet = new Set(emails);

    return emailSet.size !== emails.length;
  };

  try {
    const emails = hotel.notifEmails.map((contact) => contact.email);

    if (
      !emails.every(isValidEmail) ||
      emails.some((email) => !email) ||
      hasDuplicateEmails(emails)
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const isStep3Completed = (hotel: Partial<NewHotelType> | null) =>
  !!(hotel && hotel.experiences && hotel.experiences.length > 0);

export const NewHotel = () => {
  const history = useHistory();
  const [step, setStep] = useState<StepType>(0);
  const [newHotel, setNewHotel] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const { mutateAsync: saveHotel } = useSaveHotel();

  const isStep4Completed = (hotel: Partial<NewHotelType> | null) => {
    if (!hotel) {
      return false;
    }

    return !!(
      hotel.brief?.websiteUrl &&
      hotel.brief?.bookingUrl &&
      ((hotel.brief?.bookingComUrl &&
        hotel.brief?.bookingComUrl.match(BOOKING_COM_URL_REGEXP)) ||
        (!hotel.brief?.bookingComUrl &&
          form.getFieldValue(['brief', 'bookingComUrlCheckbox'])))
    );
  };

  const isNewHotelFilled = (
    hotel: Partial<NewHotelType> | null
  ): hotel is NewHotelType =>
    isStep1Completed(hotel) &&
    isStep2Completed(hotel) &&
    isStep3Completed(hotel) &&
    isStep4Completed(hotel);

  const getIsStepCompleted = () => {
    const stepCompletedMap = {
      0: isStep1Completed,
      1: isStep2Completed,
      2: isStep3Completed,
      3: isStep4Completed,
    };

    return stepCompletedMap[step](newHotel);
  };

  const onValuesChange = (_: unknown, values: Partial<NewHotelType>) => {
    setNewHotel({ ...newHotel, ...values });
    form.setFieldsValue(values);
  };

  const onFieldsChange = () => {
    const currentValues = form.getFieldsValue();

    setNewHotel({ ...newHotel, ...currentValues });
  };

  const isStepCompleted = getIsStepCompleted();

  const onGetLucky = (data: Partial<NewHotelType>) => {
    setNewHotel({ ...newHotel, ...data });
    form.setFieldsValue(data);
  };

  const submitNewHotel = async () => {
    if (!isNewHotelFilled(newHotel)) {
      return;
    }

    setIsLoading(true);

    const values = {
      ...newHotel,
      parentHotelId:
        newHotel.parentHotelId !== undefined && newHotel.parentHotelId !== null
          ? newHotel.parentHotelId
          : undefined,
      notifEmails: newHotel.notifEmails?.map((contact) => ({
        email: contact.email.toLowerCase(),
        bookingConfirmation: true,
        stock: true,
        billing: false,
        role: contact.role,
      })),
      channelManager:
        newHotel.channelManager === ChannelManager.NONE
          ? undefined
          : newHotel.channelManager,
      channelManagerCredentials:
        newHotel.channelManager === ChannelManager.MEWS
          ? newHotel.channelManagerCredentials
          : undefined,
      rateMode:
        newHotel.channelManager &&
        newHotel.channelManager !== ChannelManager.NONE
          ? newHotel.rateMode || RateMode.SINGLE_RATE
          : undefined,
    };

    await saveHotel(values);

    history.push('/hotels');
  };

  const renderStep = () => {
    if (step === 0) {
      return <FirstStep initializeForm={onGetLucky} />;
    }

    if (step === 1) {
      return (
        <SecondStep
          onValuesChange={onValuesChange}
          values={form.getFieldsValue()}
        />
      );
    }

    if (step === 2) {
      return <ThirdStep newHotel={newHotel} onValuesChange={onValuesChange} />;
    }

    return <FourthStep prefix={BRIEF_PREFIX} form={form} />;
  };

  const onMoveStep = (operation: 'next' | 'previous') => () => {
    const newStep = operation === 'next' ? 1 : -1;

    setStep((currentStep) => (currentStep + newStep) as StepType);
  };

  return (
    <Layout className="page__container new-hotel-form">
      <Header className="page__form-header align-items-center">
        {step === 0 ? (
          <Button onClick={() => history.goBack()}>Cancel</Button>
        ) : (
          <Button onClick={onMoveStep('previous')}>Back</Button>
        )}
        <h2 className="title">New hotel</h2>
        {step < STEP_NUMBER - 1 ? (
          <Button
            onClick={onMoveStep('next')}
            disabled={!isStepCompleted}
            type="primary"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={() => form.submit()}
            loading={isLoading}
            disabled={!isStepCompleted || isLoading}
            type="primary"
          >
            Create
          </Button>
        )}
      </Header>
      <Content className="body">
        <Steps current={step} className="steps">
          {Array(STEP_NUMBER)
            .fill(null)
            .map((val, index) => (
              <Step key={index} className="step" />
            ))}
        </Steps>
        <Form
          form={form}
          onFinish={submitNewHotel}
          onValuesChange={onValuesChange}
          onFieldsChange={onFieldsChange}
          initialValues={INITIAL_VALUE}
          scrollToFirstError={SCROLL_TO_FIRST_ERROR}
        >
          {renderStep()}
        </Form>
      </Content>
    </Layout>
  );
};
